import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate, useLocation }            from 'react-router-dom'
import {
  ShoppingCart, Search, Menu, X, Globe, ChevronDown,
  User, LogOut, ChevronRight, Sun, Moon, Monitor, Settings, Download, Share, Heart, Bell,
  Package, ArrowRight, TrendingUp,
} from 'lucide-react'
import { useAuth }          from '../contexts/AuthContext'
import { useSettings }      from '../contexts/SettingsContext'
import { useCart }          from '../contexts/CartContext'
import { useWishlist }      from '../contexts/WishlistContext'
import { useNotifications } from '../contexts/NotificationContext'
import { usePWA }           from './PWAInstall'
import { productsApi }      from '../services/api'
import { formatPrice }      from '../utils/format'
import NotificationPanel    from './NotificationPanel'
import SearchModal          from './SearchModal'
import './Navbar.css'

/* ── Nav links ── */
const navLinks = [
  { label: { fr: 'Accueil',      en: 'Home'       }, href: '/',          route: '/',          section: 'hero'     },
  { label: { fr: 'Catalogue',    en: 'Catalog'     }, href: '/catalogue', route: '/catalogue', section: null       },
  { label: { fr: 'Nos Offres',   en: 'Deals'       }, href: '/#products', route: '/',          section: 'products' },
  { label: { fr: 'Nos Magasins', en: 'Our Stores'  }, href: '/#stores',   route: '/',          section: 'stores'   },
  { label: { fr: 'À propos',     en: 'About'       }, href: '/#about',    route: '/',          section: 'about'    },
]

const THEMES = [
  { id: 'light',  Icon: Sun,     fr: 'Clair',   en: 'Light'  },
  { id: 'dark',   Icon: Moon,    fr: 'Sombre',  en: 'Dark'   },
  { id: 'system', Icon: Monitor, fr: 'Système', en: 'System' },
]

const LANGS = [
  { id: 'fr', flag: '🇫🇷', label: 'Français' },
  { id: 'en', flag: '🇬🇧', label: 'English'  },
]

function getInitials(name = '') {
  return name.split(' ').filter(Boolean).slice(0, 2).map(n => n[0].toUpperCase()).join('')
}

const AVATAR_COLORS = ['#003DA5', '#0B2B80', '#1A56CC', '#0055CC', '#1a4fa0']
function getAvatarColor(name = '') {
  const sum = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
  return AVATAR_COLORS[sum % AVATAR_COLORS.length]
}

export default function Navbar() {
  const { lang, setLang, theme, setTheme } = useSettings()
  const { user, isAuth, logout }  = useAuth()
  const { installable, isIOS, install }    = usePWA() || {}
  const { itemCount, toggleCart }          = useCart()
  const { wishlistCount }                  = useWishlist()
  const { unread: notifUnread }            = useNotifications()
  const [notifOpen, setNotifOpen]          = useState(false)
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const navigate  = useNavigate()
  const location  = useLocation()

  const [iosTooltip, setIosTooltip] = useState(false)

  const isAuthPage = location.pathname.startsWith('/auth')
  const isLanding  = location.pathname === '/'

  const [scrolled,      setScrolled]      = useState(false)
  const [navHidden,     setNavHidden]     = useState(false)
  const [menuOpen,      setMenuOpen]      = useState(false)
  const lastScrollY = useRef(0)
  const [activeSection, setActiveSection] = useState('hero')

  const [profileOpen, setProfileOpen] = useState(false)
  const [langOpen,    setLangOpen]    = useState(false)
  const [themeOpen,   setThemeOpen]   = useState(false)

  const [searchHover,   setSearchHover]   = useState(false)
  const [searchFocused, setSearchFocused] = useState(false)
  const [searchVal,     setSearchVal]     = useState('')
  const [suggestions,   setSuggestions]   = useState([])
  const [searching,     setSearching]     = useState(false)
  const [selectedIdx,   setSelectedIdx]   = useState(-1)
  const searchOpen = searchHover || searchFocused
  const showSuggest = searchOpen && searchVal.trim().length >= 2

  const profileRef   = useRef(null)
  const langRef      = useRef(null)
  const themeRef     = useRef(null)
  const searchRef    = useRef(null)
  const suggestRef   = useRef(null)
  const debounceRef  = useRef(null)

  /* ── Scroll : détection + hide-on-scroll-down ── */
  useEffect(() => {
    const fn = () => {
      const current = window.scrollY
      setScrolled(current > 20)
      // Cache la navbar quand on descend de >80px, la réaffiche quand on monte
      if (current > 80) {
        setNavHidden(current > lastScrollY.current + 4)
      } else {
        setNavHidden(false)
      }
      lastScrollY.current = current
    }
    window.addEventListener('scroll', fn, { passive: true })
    fn()
    return () => window.removeEventListener('scroll', fn)
  }, [])

  /* ── Active section (landing seulement) ── */
  useEffect(() => {
    if (!isLanding) return
    const fn = () => {
      let current = 'hero'
      for (const link of navLinks) {
        const el = document.getElementById(link.section)
        if (el && el.getBoundingClientRect().top - 100 <= 0) current = link.section
      }
      setActiveSection(current)
    }
    window.addEventListener('scroll', fn, { passive: true })
    fn()
    return () => window.removeEventListener('scroll', fn)
  }, [isLanding])

  /* ── Escape closes search / ⌘K opens SearchModal ── */
  useEffect(() => {
    const fn = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchModalOpen(v => !v)
        return
      }
      if (e.key === 'Escape') {
        searchRef.current?.blur()
        setSearchVal('')
        setSuggestions([])
        setSelectedIdx(-1)
      }
    }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [])

  /* ── Autocomplete debounce ── */
  useEffect(() => {
    clearTimeout(debounceRef.current)
    if (searchVal.trim().length < 2) { setSuggestions([]); setSearching(false); return }
    setSearching(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await productsApi.list({ search: searchVal.trim(), per_page: 6 })
        setSuggestions(res.data ?? [])
      } catch { setSuggestions([]) }
      finally { setSearching(false) }
    }, 280)
    return () => clearTimeout(debounceRef.current)
  }, [searchVal])

  /* ── Click outside closes dropdowns ── */
  useEffect(() => {
    const fn = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false)
      if (langRef.current    && !langRef.current.contains(e.target))    setLangOpen(false)
      if (themeRef.current   && !themeRef.current.contains(e.target))   setThemeOpen(false)
    }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  /* Fermer menu mobile au changement de route */
  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  const t = (obj) => obj[lang]

  /** Navigation intelligente : scroll sur landing, navigate() ailleurs */
  const handleNavClick = (e, link) => {
    e.preventDefault()
    setMenuOpen(false)

    // Lien avec sa propre route (ex: /catalogue)
    if (link.route !== '/') {
      navigate(link.route)
      return
    }

    // Pas sur la landing → navigate vers la landing (hash géré par le browser)
    if (!isLanding) {
      navigate(link.section ? `/#${link.section}` : '/')
      return
    }

    // Sur la landing → smooth-scroll vers la section
    if (link.section) {
      const target = document.getElementById(link.section)
      if (target) {
        const navbarH = 72
        const top = target.getBoundingClientRect().top + window.scrollY - navbarH
        window.scrollTo({ top, behavior: 'smooth' })
        setTimeout(() => window.__revealCheck?.(), 400)
        setTimeout(() => window.__revealCheck?.(), 900)
      }
    } else {
      navigate('/')
    }
  }

  /** Active state : section active sur landing, route active ailleurs */
  const isNavActive = (link) => {
    if (!isLanding) {
      return link.route !== '/' && location.pathname.startsWith(link.route)
    }
    return activeSection === link.section
  }

  const closeSearch = useCallback(() => {
    setSearchVal(''); setSuggestions([]); setSelectedIdx(-1)
    setSearchFocused(false); setSearchHover(false)
    searchRef.current?.blur()
  }, [])

  const handleSearch = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx(i => Math.min(i + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx(i => Math.max(i - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedIdx >= 0 && suggestions[selectedIdx]) {
        navigate(`/produits/${suggestions[selectedIdx].slug}`)
        closeSearch()
      } else if (searchVal.trim()) {
        navigate(`/catalogue?search=${encodeURIComponent(searchVal.trim())}`)
        closeSearch()
      }
    }
  }

  const handleSuggestionClick = (slug) => {
    navigate(`/produits/${slug}`)
    closeSearch()
  }

  const handleLogout = async () => {
    setProfileOpen(false)
    // Deconnexion locale quoi qu'il arrive : si l'appel serveur echoue, la
    // session doit disparaitre de cet appareil malgre tout.
    try { await logout() } catch (_) { /* voir ci-dessus */ }
    navigate('/')
  }

  const initials    = user ? getInitials(user.name) : ''
  const avatarColor = user ? getAvatarColor(user.name) : '#003DA5'
  const firstName   = user?.name?.split(' ')[0] || ''

  /* Sur les pages auth, le Navbar est toujours en mode glass */
  const showGlass = scrolled || isAuthPage

  const ThemeIcon = (THEMES.find(th => th.id === theme) || THEMES[2]).Icon

  return (
    <>
      <nav className={`navbar ${showGlass ? 'scrolled' : ''} ${navHidden && !menuOpen ? 'navbar--hidden' : ''}`}>
        <div className="navbar-inner">

          {/* ── Logo ── */}
          <Link to="/" className="navbar-logo" aria-label="ABBUSINESS — Accueil">
            <img src="/logo.png" alt="ABBUSINESS" className="navbar-logo-img" />
          </Link>

          {/* ── Nav links ── */}
          <ul className="navbar-links">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className={`nav-link ${isNavActive(link) ? 'active' : ''}`}
                  onClick={(e) => handleNavClick(e, link)}
                >
                  {t(link.label)}
                  <span className="nav-indicator" />
                </a>
              </li>
            ))}
          </ul>

          {/* ── Actions ── */}
          <div className="navbar-actions">

            {/* Search */}
            <div
              className="search-hover-wrap"
              ref={suggestRef}
              onMouseEnter={() => setSearchHover(true)}
              onMouseLeave={() => { if (!searchFocused) setSearchHover(false) }}
            >
              <div className={`search-expand ${searchOpen ? 'open' : ''}`}>
                <Search size={13} className="se-icon" />
                <input
                  ref={searchRef}
                  type="text"
                  value={searchVal}
                  onChange={(e) => { setSearchVal(e.target.value); setSelectedIdx(-1) }}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => {
                    // Délai pour laisser le click sur une suggestion s'exécuter d'abord
                    setTimeout(() => {
                      setSearchFocused(false)
                      setSearchHover(false)
                      setSearchVal('')
                      setSuggestions([])
                      setSelectedIdx(-1)
                    }, 160)
                  }}
                  onKeyDown={handleSearch}
                  placeholder={lang === 'fr' ? 'Rechercher…' : 'Search…'}
                  className="se-input"
                  autoComplete="off"
                />
                {searchVal && (
                  <button
                    className="se-clear"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setSearchVal(''); setSuggestions([]); searchRef.current?.focus() }}
                  >
                    <X size={11} />
                  </button>
                )}
              </div>
              <button className="action-btn" onClick={() => setSearchModalOpen(true)} aria-label="Rechercher (⌘K)">
                <Search size={18} />
              </button>


              {/* Suggestions dropdown */}
              {showSuggest && (
                <div className="se-dropdown" onMouseDown={(e) => e.preventDefault()}>
                  {searching && suggestions.length === 0 && (
                    <div className="se-dd-loading">
                      <span className="se-dd-spinner" />
                      {lang === 'fr' ? 'Recherche…' : 'Searching…'}
                    </div>
                  )}
                  {!searching && suggestions.length === 0 && (
                    <div className="se-dd-empty">
                      <Package size={18} />
                      {lang === 'fr' ? 'Aucun résultat' : 'No results'}
                    </div>
                  )}
                  {suggestions.map((p, i) => (
                    <button
                      key={p.id ?? p.slug}
                      className={`se-dd-item ${selectedIdx === i ? 'selected' : ''}`}
                      onClick={() => handleSuggestionClick(p.slug)}
                    >
                      <div className="se-dd-img">
                        {p.primary_image
                          ? <img src={p.primary_image} alt={p.name?.[lang] ?? ''} loading="lazy" />
                          : <Package size={14} />}
                      </div>
                      <div className="se-dd-info">
                        <span className="se-dd-name">{p.name?.[lang] ?? p.name?.fr}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span className="se-dd-price">{formatPrice(p.effective_price ?? p.price)}</span>
                          {(p.stock != null && p.stock < 5 && p.stock > 0) && (
                            <span className="se-dd-stock-low">Stock: {p.stock}</span>
                          )}
                          {(p.stock != null && p.stock === 0) && (
                            <span className="se-dd-stock-out">Épuisé</span>
                          )}
                        </div>
                      </div>
                      <ArrowRight size={13} className="se-dd-arrow" />
                    </button>
                  ))}
                  {suggestions.length > 0 && (
                    <button
                      className="se-dd-all"
                      onClick={() => {
                        navigate(`/catalogue?search=${encodeURIComponent(searchVal.trim())}`)
                        closeSearch()
                      }}
                    >
                      <TrendingUp size={13} />
                      {lang === 'fr' ? `Voir tous les résultats pour "${searchVal}"` : `See all results for "${searchVal}"`}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Langue */}
            <div className="dd-wrap" ref={langRef}>
              <button
                className={`action-btn lang-btn ${langOpen ? 'open' : ''}`}
                onClick={() => { setLangOpen(v => !v); setThemeOpen(false) }}
                aria-label="Langue"
              >
                <Globe size={15} />
                <span className="lang-label">{lang.toUpperCase()}</span>
                <ChevronDown size={12} className={`dd-chevron ${langOpen ? 'open' : ''}`} />
              </button>
              {langOpen && (
                <div className="mini-dropdown">
                  {LANGS.map((l) => (
                    <button
                      key={l.id}
                      className={`md-opt ${lang === l.id ? 'active' : ''}`}
                      onClick={() => { setLang(l.id); setLangOpen(false) }}
                    >
                      <span className="md-flag">{l.flag}</span>
                      {l.label}
                      {lang === l.id && <span className="md-check">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Thème */}
            <div className="dd-wrap" ref={themeRef}>
              <button
                className={`action-btn ${themeOpen ? 'open' : ''}`}
                onClick={() => { setThemeOpen(v => !v); setLangOpen(false) }}
                aria-label="Affichage"
              >
                <ThemeIcon size={18} />
              </button>
              {themeOpen && (
                <div className="mini-dropdown theme-dd">
                  {THEMES.map(({ id, Icon: Ic, fr, en }) => (
                    <button
                      key={id}
                      className={`md-opt ${theme === id ? 'active' : ''}`}
                      onClick={() => { setTheme(id); setThemeOpen(false) }}
                    >
                      <Ic size={14} />
                      {lang === 'fr' ? fr : en}
                      {theme === id && <span className="md-check">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Installer l'app PWA ── */}
            {installable && !isIOS && (
              <button
                className="action-btn pwa-install-btn"
                onClick={install}
                aria-label="Installer l'application"
                title={lang === 'fr' ? "Installer l'application" : 'Install app'}
              >
                <Download size={17} />
              </button>
            )}

            {/* iOS — bouton + tooltip */}
            {installable && isIOS && (
              <div className="dd-wrap" style={{ position: 'relative' }}>
                <button
                  className="action-btn pwa-install-btn"
                  onClick={() => setIosTooltip(v => !v)}
                  aria-label="Installer sur iOS"
                  title="Ajouter à l'écran d'accueil"
                >
                  <Download size={17} />
                </button>
                {iosTooltip && (
                  <div className="pwa-ios-tooltip">
                    <button
                      className="pwa-ios-tooltip__close"
                      onClick={() => setIosTooltip(false)}
                    >
                      <X size={13} />
                    </button>
                    <div className="pwa-ios-tooltip__title">
                      {lang === 'fr' ? "Installer l'app" : 'Install App'}
                    </div>
                    <ol className="pwa-ios-tooltip__steps">
                      <li>
                        {lang === 'fr' ? 'Appuie sur' : 'Tap'}{' '}
                        <Share size={13} style={{ verticalAlign: 'middle', color: '#007AFF' }} />{' '}
                        <strong>{lang === 'fr' ? 'Partager' : 'Share'}</strong>
                      </li>
                      <li>
                        <strong>{lang === 'fr' ? '"Sur l\'écran d\'accueil"' : '"Add to Home Screen"'}</strong>
                      </li>
                      <li>
                        <strong>{lang === 'fr' ? '"Ajouter"' : '"Add"'}</strong>
                      </li>
                    </ol>
                  </div>
                )}
              </div>
            )}

            {/* Notifications */}
            {isAuth && (
              <button
                className="action-btn cart-btn"
                onClick={() => setNotifOpen(v => !v)}
                aria-label="Notifications"
                style={{ position: 'relative' }}
              >
                <Bell size={18} />
                {notifUnread > 0 && (
                  <span className="cart-badge" key={notifUnread}>{notifUnread > 9 ? '9+' : notifUnread}</span>
                )}
              </button>
            )}

            {/* Wishlist */}
            {isAuth && (
              <Link to="/ma-liste" className="action-btn cart-btn" aria-label="Ma liste de souhaits" style={{ position:'relative' }}>
                <Heart size={18} />
                {wishlistCount > 0 && (
                  <span className="cart-badge" key={wishlistCount}>{wishlistCount > 99 ? '99+' : wishlistCount}</span>
                )}
              </Link>
            )}

            {/* Panier */}
            <button className="action-btn cart-btn" onClick={toggleCart} aria-label="Panier">
              <ShoppingCart size={18} />
              {itemCount > 0 && (
                <span className="cart-badge" key={itemCount}>{itemCount > 99 ? '99+' : itemCount}</span>
              )}
            </button>

            {/* Auth */}
            {isAuth && user ? (
              <div className="dd-wrap" ref={profileRef}>
                <button className="profile-btn" onClick={() => setProfileOpen(v => !v)}>
                  <div className="profile-avatar" style={{ background: avatarColor }}>{initials}</div>
                  <span className="profile-name">{firstName}</span>
                  <ChevronDown size={13} className={`dd-chevron ${profileOpen ? 'open' : ''}`} />
                </button>

                {profileOpen && (
                  <div className="profile-dropdown">
                    <div className="pd-header">
                      <div className="pd-avatar" style={{ background: avatarColor }}>{initials}</div>
                      <div>
                        <div className="pd-name">{user.name}</div>
                        <div className="pd-email">{user.email}</div>
                      </div>
                    </div>
                    <ul className="pd-menu">
                      <li>
                        <Link to="/profile" onClick={() => setProfileOpen(false)}>
                          <User size={14} />
                          {lang === 'fr' ? 'Mon profil' : 'My profile'}
                        </Link>
                      </li>
                      <li>
                        <Link to="/commandes" onClick={() => setProfileOpen(false)}>
                          <ShoppingCart size={14} />
                          {lang === 'fr' ? 'Mes commandes' : 'My orders'}
                        </Link>
                      </li>
                      <li>
                        <Link to="/ma-liste" onClick={() => setProfileOpen(false)}>
                          <Heart size={14} />
                          {lang === 'fr' ? 'Ma liste de souhaits' : 'My wishlist'}
                        </Link>
                      </li>
                      {user.role === 'admin' && (
                        <li>
                          <Link to="/admin" onClick={() => setProfileOpen(false)}>
                            <Settings size={14} />
                            {lang === 'fr' ? 'Administration' : 'Admin'}
                          </Link>
                        </li>
                      )}
                    </ul>
                    <button className="pd-logout" onClick={handleLogout}>
                      <LogOut size={14} />
                      {lang === 'fr' ? 'Se déconnecter' : 'Logout'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="navbar-auth-btns">
                <Link to="/auth/login" className="navbar-login-btn">
                  {lang === 'fr' ? 'Connexion' : 'Login'}
                </Link>
                <Link to="/auth/register" className="navbar-cta btn btn-primary">
                  <User size={14} />
                  {lang === 'fr' ? "S'inscrire" : 'Sign up'}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="mobile-toggle" onClick={() => setMenuOpen(v => !v)} aria-label="Menu">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* ── Notification Panel ── */}
      <NotificationPanel open={notifOpen} onClose={() => setNotifOpen(false)} />

      {/* ── Global Search Modal (⌘K) ── */}
      <SearchModal
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        lang={lang}
      />

      {/* ── Menu mobile ── */}
      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        {isAuth && user && (
          <div className="mobile-user">
            <div className="profile-avatar" style={{ background: avatarColor, width: 44, height: 44, fontSize: 15 }}>
              {initials}
            </div>
            <div>
              <div className="pd-name">{user.name}</div>
              <div className="pd-email">{user.email}</div>
            </div>
          </div>
        )}

        <ul className="mobile-links">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className={isNavActive(link) ? 'active' : ''}
                onClick={(e) => handleNavClick(e, link)}
              >
                {t(link.label)}<ChevronRight size={15} />
              </a>
            </li>
          ))}
          {isAuth && user && (
            <>
              <li>
                <Link to="/profile" onClick={() => setMenuOpen(false)}>
                  {lang === 'fr' ? 'Mon profil' : 'My profile'}<ChevronRight size={15} />
                </Link>
              </li>
              <li>
                <Link to="/commandes" onClick={() => setMenuOpen(false)}>
                  {lang === 'fr' ? 'Mes commandes' : 'My orders'}<ChevronRight size={15} />
                </Link>
              </li>
              {user.role === 'admin' && (
                <li>
                  <Link to="/admin" onClick={() => setMenuOpen(false)}>
                    {lang === 'fr' ? 'Administration' : 'Admin'}<ChevronRight size={15} />
                  </Link>
                </li>
              )}
            </>
          )}
        </ul>

        <div className="mobile-actions">
          <div className="mobile-search">
            <Search size={15} className="mob-search-icon" />
            <input
              type="text"
              placeholder={lang === 'fr' ? 'Rechercher…' : 'Search…'}
              className="mob-search-input"
            />
          </div>

          <div className="mobile-settings">
            <p className="mobile-settings-label">{lang === 'fr' ? 'Langue' : 'Language'}</p>
            <div className="mobile-row">
              {LANGS.map((l) => (
                <button
                  key={l.id}
                  className={`md-opt ${lang === l.id ? 'active' : ''}`}
                  onClick={() => setLang(l.id)}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  <span className="md-flag">{l.flag}</span>
                  {l.label}
                  {lang === l.id && <span className="md-check">✓</span>}
                </button>
              ))}
            </div>
            <p className="mobile-settings-label" style={{ marginTop: 12 }}>
              {lang === 'fr' ? 'Affichage' : 'Display'}
            </p>
            <div className="mobile-row">
              {THEMES.map(({ id, Icon: Ic, fr, en }) => (
                <button
                  key={id}
                  className={`sd-theme-btn ${theme === id ? 'active' : ''}`}
                  onClick={() => setTheme(id)}
                  style={{ flex: 1 }}
                >
                  <div className="sd-theme-icon"><Ic size={15} /></div>
                  <span>{lang === 'fr' ? fr : en}</span>
                </button>
              ))}
            </div>
          </div>

          {isAuth && user ? (
            <button
              className="mob-logout-btn"
              onClick={handleLogout}
            >
              <LogOut size={15} />
              {lang === 'fr' ? 'Se déconnecter' : 'Logout'}
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Link
                to="/auth/login"
                className="mob-login-btn"
                onClick={() => setMenuOpen(false)}
              >
                {lang === 'fr' ? 'Connexion' : 'Login'}
              </Link>
              <Link
                to="/auth/register"
                className="mob-register-btn"
                onClick={() => setMenuOpen(false)}
              >
                <User size={15} />
                {lang === 'fr' ? 'Créer un compte' : 'Sign up'}
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
